import {gql} from '@apollo/client';

import {
  AntibodyContainmentQuery,
  AntibodyContainmentQueryVariables,
} from 'antibodies/hooks/__generated/AntibodyContainmentQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {IsContainedInComplexPolymerData} from 'modbio/complex-polymers/ComplexPolymerConstants';

export const ANTIBODY_CONTAINMENT_QUERY = gql`
  query AntibodyContainmentQuery($antibodyId: StrictString!) @skipAudit @engComponent(name: ANTIBODIES) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        isContainedInComplexPolymerData {
          isContained
          errorMessage
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AntibodyContainmentResult {
  isPending: boolean;
  containedData: IsContainedInComplexPolymerData | null;
}

/**
 * Returns whether the protein is a component of a complex polymer. This is a separate query so that views
 * that never edit the protein (e.g. embedded maps) don't pay for the check. `containedData` is null while
 * pending and when the query fails or the protein is inaccessible.
 */
export function useAntibodyContainmentQuery(antibodyId: string): AntibodyContainmentResult {
  const result = useBenchlingQuery<AntibodyContainmentQuery, AntibodyContainmentQueryVariables>(
    ANTIBODY_CONTAINMENT_QUERY,
    {variables: {antibodyId}}
  );
  if (result.type === QueryResultType.PENDING) {
    return {isPending: true, containedData: null};
  }
  if (result.type !== QueryResultType.SUCCESS || isInaccessible(result.data.antibody)) {
    return {isPending: false, containedData: null};
  }
  return {isPending: false, containedData: result.data.antibody.isContainedInComplexPolymerData};
}
